import { available, isPositiveU64, isU64, PRICE_SCALE_D9, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

export type OutcomeMarketCapAtPriceInput =
  | {
      poolModel: 'bonding_curve';
      priceD9: bigint;
      initialOutcomeSupplyD6: bigint;
      virtualQuoteReserveD6: bigint;
    }
  | {
      poolModel: 'cp_swap';
      priceD9: bigint;
      /** Indexed outcome-mint supply; never BC tracked sold supply. */
      indexedOutcomeMintSupplyD6: bigint;
    };

export interface BondingCurveMarketCapFromQuoteReserveInput {
  poolQuoteReserveD6: bigint;
  virtualQuoteReserveD6: bigint;
}

export interface ScaleFixedSupplyMarketCapInput {
  snapshotMarketCapD6: bigint;
  latestPriceD9: bigint;
  snapshotPriceD9: bigint;
}

/**
 * Market cap at a D9 price using lifecycle-correct BC or migrated-winner CP supply.
 *
 * The CP input is deliberately neutral at this arithmetic boundary. Callers must
 * supply the indexed migrated-winner mint supply, not infer chain-current supply
 * for terminal losing outcomes.
 */
export function outcomeMarketCapAtPriceD6(
  input: OutcomeMarketCapAtPriceInput,
): PoolMathResult<bigint> {
  if (!isU64(input.priceD9)) return unavailable('invalid_amount');

  if (input.poolModel === 'cp_swap') {
    if (!isPositiveU64(input.indexedOutcomeMintSupplyD6)) {
      return unavailable('invalid_reserves');
    }
    return checkedMarketCap((input.priceD9 * input.indexedOutcomeMintSupplyD6) / PRICE_SCALE_D9);
  }

  if (!isPositiveU64(input.initialOutcomeSupplyD6) || !isPositiveU64(input.virtualQuoteReserveD6)) {
    return unavailable('invalid_reserves');
  }
  const valueD6 =
    (input.priceD9 * input.initialOutcomeSupplyD6) / PRICE_SCALE_D9 - input.virtualQuoteReserveD6;
  return checkedMarketCap(valueD6 > 0n ? valueD6 : 0n);
}

/** Effective BC market cap from the current virtual-inclusive quote reserve. */
export function bondingCurveMarketCapFromQuoteReserveD6(
  input: BondingCurveMarketCapFromQuoteReserveInput,
): PoolMathResult<bigint> {
  if (
    !isPositiveU64(input.poolQuoteReserveD6) ||
    !isPositiveU64(input.virtualQuoteReserveD6) ||
    input.poolQuoteReserveD6 < input.virtualQuoteReserveD6
  ) {
    return unavailable('invalid_reserves');
  }
  const valueD6 =
    (input.poolQuoteReserveD6 * input.poolQuoteReserveD6) / input.virtualQuoteReserveD6 -
    input.virtualQuoteReserveD6;
  return checkedMarketCap(valueD6);
}

/** Reprices a fixed-supply market cap, rounding an exact half D6 unit upward. */
export function scaleFixedSupplyMarketCapD6(
  input: ScaleFixedSupplyMarketCapInput,
): PoolMathResult<bigint> {
  if (
    !isU64(input.snapshotMarketCapD6) ||
    !isPositiveU64(input.latestPriceD9) ||
    !isPositiveU64(input.snapshotPriceD9)
  ) {
    return unavailable('invalid_amount');
  }
  const valueD6 =
    (input.snapshotMarketCapD6 * input.latestPriceD9 + input.snapshotPriceD9 / 2n) /
    input.snapshotPriceD9;
  return checkedMarketCap(valueD6);
}

function checkedMarketCap(valueD6: bigint): PoolMathResult<bigint> {
  return isU64(valueD6) ? available(valueD6) : unavailable('invalid_amount');
}
